import {
	useEffect,
	useRef,
	ComponentPropsWithoutRef,
	forwardRef,
	Ref,
	useImperativeHandle,
} from "react";
import { ChallengeScheme } from "client-v2";

const ChallengeCanvas = forwardRef((props: Props, ref: Ref<CanvasRef>) => {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const height = 1920;
	const width = 1080;

	useImperativeHandle(ref, () => ({
		toDataURL() {
			if (canvasRef.current) {
				return canvasRef.current.toDataURL("image/png");
			} else return undefined;
		},
	}));

	useEffect(() => {
		const canvas = canvasRef.current;
		if (canvas) {
			const ctx = canvas.getContext("2d");
			if (ctx) {
				//Generate the image
				var img = new Image();
				img.crossOrigin = "anonymous";
				img.src = props.image;
				img.onload = (e: any) => {
					ctx.drawImage(img, 0, 0, width, height);

					//Add title
					const title =
						props.challenge.type === "dream"
							? props.challenge.to
								? `¡He ayudado a cumplir el sueño de ${props.challenge.to.title}!`
								: "¡He ayudado a cumplir un sueño!"
							: "¡Feliz cumpleaños!";
					setText(ctx, {
						text: title,
						textColor: props.textColor,
						fontSize: 64,
						textWidth: 856,
						y: 240,
					});
					//Add challenge title
					setText(ctx, {
						text: props.challenge.title,
						textColor: props.textColor,
						fontSize: 36,
						textWidth: 800,
						y: 1290,
					});

					// Add challenge image
					if (props.challenge.image?.url) {
						const challengeImg = new Image();
						challengeImg.src = props.challenge.image.url;
						challengeImg.onload = () => {
							const radius = 24;
							const x = 128;
							const y = 500;
							const w = 824;
							const h = 720;

							// Calcular relaciones de aspecto
							const imgAspect =
								challengeImg.width / challengeImg.height;
							const canvasAspect = w / h;

							let sourceX = 0;
							let sourceY = 0;
							let sourceWidth = challengeImg.width;
							let sourceHeight = challengeImg.height;

							if (imgAspect > canvasAspect) {
								// Imagen más ancha que el contenedor
								sourceWidth =
									challengeImg.height * canvasAspect;
								sourceX =
									(challengeImg.width - sourceWidth) / 2;
							} else {
								// Imagen más alta que el contenedor
								sourceHeight =
									challengeImg.width / canvasAspect;
								sourceY =
									(challengeImg.height - sourceHeight) / 2;
							}

							ctx.save();

							// Borde redondeado
							ctx.beginPath();
							ctx.moveTo(x + radius, y);
							ctx.lineTo(x + w - radius, y);
							ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
							ctx.lineTo(x + w, y + h - radius);
							ctx.quadraticCurveTo(
								x + w,
								y + h,
								x + w - radius,
								y + h
							);
							ctx.lineTo(x + radius, y + h);
							ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
							ctx.lineTo(x, y + radius);
							ctx.quadraticCurveTo(x, y, x + radius, y);
							ctx.closePath();
							ctx.clip();

							// Dibujo con recorte tipo object-fit: cover
							ctx.drawImage(
								challengeImg,
								sourceX,
								sourceY,
								sourceWidth,
								sourceHeight,
								x,
								y,
								w,
								h
							);

							ctx.restore();
						};
					}
				};
			}
		}
	}, [props.image]);

	const setText = (
		ctx: CanvasRenderingContext2D,
		data: {
			text: string;
			fontSize: number;
			textWidth: number;
			textColor: string;
			y: number;
		}
	) => {
		ctx.font = `bold ${data.fontSize}px Poppins`;
		ctx.textAlign = "center";
		const words = data.text.split(" ");
		const lines: string[] = [];
		let currentLine = "";

		words.forEach((word) => {
			if (ctx.measureText(currentLine + word).width < data.textWidth) {
				currentLine += word + " ";
			} else {
				lines.push(currentLine.trim());
				currentLine = word + " ";
			}
		});
		if (currentLine) {
			lines.push(currentLine.trim());
		}

		lines.slice(0, 2).forEach((line, index) => {
			ctx.fillText(
				line,
				width / 2,
				data.y + index * (data.fontSize + 14),
				data.textWidth
			);
		});
	};

	return <canvas ref={canvasRef} width={width} height={height} {...props} />;
});
export default ChallengeCanvas;
export interface Props extends ComponentPropsWithoutRef<"canvas"> {
	image: string;
	textColor: string;
	design: string;
	challenge: ChallengeScheme;
}
export interface CanvasRef {
	toDataURL: () => string | undefined;
}
